/**
 * Presets de venue por parámetro de URL — ?venue=<clave> (alias: ?preset=<clave>)
 *
 * Permite llegar al cotizador con campos del paso 1 ya seteados para sedes
 * conocidas. Ej.: /cotizador?venue=esh pre-llena sede, dirección y asistentes
 * del Ex Convento de San Hipólito, y encadena el deep-link de recinto existente
 * (sessionStorage "recinto_token") para que el paso 2 pre-seleccione el recinto
 * con sus cuotas obligatorias.
 *
 * Mismo patrón de persistencia que captureRecinto/captureUTMParams: el token
 * sobrevive navegación interna vía sessionStorage, pero el preset solo se
 * re-aplica sobre el formulario cuando viene explícito en la URL (para no pisar
 * ediciones del usuario en visitas posteriores dentro de la misma pestaña).
 */

export const VENUE_PRESETS = {
  esh: {
    // Campos del eventForm que el preset fija.
    eventFields: {
      sede: "Ex Convento de San Hipólito",
      direccion: "Av. Hidalgo 107, Col. Guerrero, Cuauhtémoc, 06300 Ciudad de México, CDMX",
      asistentes: 200,
    },
    // Token del recinto en el catálogo backend (RecintoConectividadConfig.token)
    // — el effect de deep-link existente lo usa para pre-seleccionar el salón.
    recintoToken: "exconvento",
  },
};

const APPLIED_KEY = "venue_preset_applied";
const TOKEN_KEY = "venue_preset";

/**
 * Lee el preset de la URL (o de sessionStorage si se navegó internamente) y
 * decide si debe aplicarse al formulario en este montaje.
 *
 * @returns {{ key: string, preset: Object, shouldApply: boolean } | null}
 *   null si no hay preset válido. `shouldApply` es true cuando el preset viene
 *   explícito en la URL, o cuando quedó pendiente de aplicar (llegó por
 *   sessionStorage y aún no se había aplicado).
 */
export function capturePreset() {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get("venue") || params.get("preset") || "";
  const stored = sessionStorage.getItem(TOKEN_KEY) || "";
  const key = fromUrl || stored;

  const preset = VENUE_PRESETS[key];
  if (!preset) return null;

  sessionStorage.setItem(TOKEN_KEY, key);

  // Encadena el deep-link de recinto: captureRecinto() leerá este token cuando
  // el catálogo llegue y pre-seleccionará el salón correspondiente. Un preset
  // explícito en la URL SOBREESCRIBE un recinto_token previo de la pestaña —
  // si no, la sede/dirección del preset quedarían desincronizadas del salón
  // realmente seleccionado en el paso 2.
  if (preset.recintoToken && (fromUrl || !sessionStorage.getItem("recinto_token"))) {
    sessionStorage.setItem("recinto_token", preset.recintoToken);
  }

  // UNA sola vez por sesión, aunque el parámetro siga en la URL. Antes se
  // re-aplicaba con cada montaje que trajera `?venue=`, así que un usuario que
  // corregía la sede o los asistentes y recargaba (o volvía a la pestaña) veía
  // sus datos reemplazados por los del preset — y `asistentes` alimenta el
  // cálculo de Mbps/APs, así que terminaba cotizando sobre un dato que él ya
  // había corregido.
  const alreadyApplied = sessionStorage.getItem(APPLIED_KEY) === key;
  return { key, preset, shouldApply: !alreadyApplied };
}

/**
 * Devuelve los campos del preset que se pueden aplicar SIN destruir lo que el
 * usuario ya escribió: solo los que siguen exactamente con su valor inicial.
 *
 * @param {Object} form     - formulario actual
 * @param {Object} initial  - formulario inicial (valores de fábrica)
 * @param {Object} fields   - campos que el preset quiere fijar
 */
export function camposAplicables(form, initial, fields) {
  const out = {};
  Object.entries(fields).forEach(([k, v]) => {
    const actual = form?.[k];
    const intacto =
      actual === undefined ||
      actual === "" ||
      actual === null ||
      actual === initial?.[k];
    if (intacto) out[k] = v;
  });
  return out;
}

/**
 * Marca el preset como aplicado y quita el parámetro de la URL, para que una
 * recarga no vuelva a dispararlo. Se conserva el resto del query string
 * (UTMs, ?recinto=) porque otras piezas los leen.
 */
export function markPresetApplied(key) {
  sessionStorage.setItem(APPLIED_KEY, key);
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has("venue") || url.searchParams.has("preset")) {
      url.searchParams.delete("venue");
      url.searchParams.delete("preset");
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }
  } catch {
    /* history bloqueado: no es crítico, el flag de sessionStorage ya protege */
  }
}
